import React, { type HTMLAttributes } from 'react';
import classnames from 'classnames';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import FakeButton from './FakeButton';
import s from './PlainButtonOrLink.scss';

export type Props = {
  fillContainerWidth?: boolean;
  ['data-role']?: string;
  biData?: BiEvent;
  stopPropagation?: boolean;
  innerRef?: (node: HTMLElement | null) => void;
  /**
   * Indicates an action related to this button is being processed. Values of `true` or `false` will attach the necessary
   * aria attributes for screenreader users
   */
  loading?: boolean;
  /**
   * Renders a `<span />` with the correct event handlers and role instead of a `<button />`.
   * Only use this if you know what you're doing accessibility wise. Real buttons are always preferred.
   */
  isFakeButton?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

const PlainButtonOrLink: React.FC<Props> = ({
  className,
  isFakeButton,
  onClick = () => null,
  loading,
  ...rest
}) => {
  const href = (rest as React.AnchorHTMLAttributes<HTMLAnchorElement>).href;
  const classNames = classnames(
    href ? 'reset-link' : 'reset-button',
    isFakeButton && s['fake-button'],
    className
  );

  /**
   * If we're told this button has a loading state (fetching data, completing an action), we should make sure screenreader
   * users are updated when it finishes loading. But only if their focus is currently on the button.
   */
  const loadingProps: Pick<
    HTMLAttributes<any>,
    'aria-live' | 'aria-busy' | 'aria-atomic'
  > = typeof loading !== 'undefined'
    ? {
        'aria-live': 'off',
        'aria-atomic': 'true',
        'aria-busy': loading,
      }
    : {};

  const props = {
    className: classNames,
    ...loadingProps,
    ...rest,
  };

  return href ? (
    <BiLink onClick={onClick} {...props} />
  ) : !isFakeButton ? (
    <BiButton onClick={onClick} {...props} />
  ) : (
    // There are some instances where we need to render a button inside of a button. This is not correct HTML.
    // To solve this use-case, the following is a fake button using role="button" and the correct event handlers.
    <FakeButton tabIndex={0} onClick={onClick} {...props} />
  );
};
PlainButtonOrLink.displayName = 'PlainButtonOrLink';

export default PlainButtonOrLink;
